/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import type { ChartPoint } from '@sportninja/common/features/profile/stats/types'
import { buildPointTooltip } from '@sportninja/common/features/profile/stats/utils/pointTooltip'
import { useRef, useState, type MouseEvent } from 'react'

import { paragraphXSmall } from 'src/components/css'

import ChartPointTooltip from './ChartPointTooltip'

interface GamesPlayedStripProps {
  points: ChartPoint[]
  /** Bar fill colour. */
  accent: string
  /** Horizontal inset (CSS length) so the bars sit under a chart's plot area. */
  insetLeft?: string
  insetRight?: string
}

/** Height of the tallest bar in px; room for its count label is added on top. */
const BAR_HEIGHT = 48
/** Room above the tallest bar for its count label, so it never clips. */
const LABEL_ROW_HEIGHT = 16
const LABEL_GAP = 4
const BASELINE_WIDTH = 1
const STRIP_BODY_HEIGHT = BAR_HEIGHT + LABEL_ROW_HEIGHT + LABEL_GAP

/**
 * The strip's full rendered height in px (bars, count labels and baseline).
 * A chart that hides the strip grows by this much so its block keeps its size.
 */
export const GAMES_STRIP_HEIGHT = STRIP_BODY_HEIGHT + BASELINE_WIDTH

/** The games-played line plots the game count itself. */
const GAMES_ABBR = 'GP'

interface BarHover {
  index: number
  /** Bubble anchor in px from the strip's top-left: above the bar's count. */
  left: number
  top: number
}

/** One bar per period, height proportional to games played, its count on top. */
const GamesPlayedStrip = ({
  points,
  accent,
  insetLeft = '0',
  insetRight = '0',
}: GamesPlayedStripProps) => {
  'use memo'

  const [hovered, setHovered] = useState<BarHover | null>(null)
  const stripRef = useRef<HTMLDivElement>(null)

  const maxGames = Math.max(0, ...points.map((p) => p.gamesPlayed))
  const hoveredPoint = hovered ? points[hovered.index] : undefined

  // Anchor the bubble over the hovered bar's count label, in the strip's own
  // coordinates (the strip is the bubble's positioning context).
  const showHover = (index: number, event: MouseEvent<HTMLDivElement>) => {
    const strip = stripRef.current?.getBoundingClientRect()
    if (!strip) {
      return
    }
    const cell = event.currentTarget.getBoundingClientRect()
    const top =
      event.currentTarget.firstElementChild?.getBoundingClientRect().top ??
      cell.top
    setHovered({
      index,
      left: cell.left - strip.left + cell.width / 2,
      top: top - strip.top,
    })
  }

  return (
    <div
      ref={stripRef}
      css={stripStyles(insetLeft, insetRight)}
      aria-label='Games played per period'
      role='img'
    >
      {points.map((point) => {
        const hasGames = point.gamesPlayed > 0 && maxGames > 0
        return (
          <div
            key={point.index}
            css={barCellStyles}
            onMouseEnter={
              hasGames ? (event) => showHover(point.index, event) : undefined
            }
            onMouseLeave={() => setHovered(null)}
          >
            {hasGames && (
              <span aria-hidden='true' css={countStyles}>
                {point.gamesPlayed}
              </span>
            )}
            {hasGames && (
              <div
                css={barStyles(
                  accent,
                  (point.gamesPlayed / maxGames) * BAR_HEIGHT
                )}
              />
            )}
          </div>
        )
      })}
      {hovered && hoveredPoint ? (
        <ChartPointTooltip
          tooltip={buildPointTooltip(hoveredPoint, GAMES_ABBR)}
          color={accent}
          left={hovered.left}
          top={hovered.top}
        />
      ) : null}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const stripStyles = (insetLeft: string, insetRight: string) => css`
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: ${STRIP_BODY_HEIGHT}px;
  margin: 0 ${insetRight} 0 ${insetLeft};
  /* One continuous baseline the bars stand on; a period with no games is
     simply empty above it. */
  border-bottom: ${BASELINE_WIDTH}px solid ${colors.SOFT_STEEL};
`

/** One period: the bar sits on the baseline with its count directly on top. */
const barCellStyles = css`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: ${LABEL_GAP}px;
  flex: 1;
  min-width: 0;
  height: 100%;
`

const countStyles = css`
  ${paragraphXSmall}
  height: ${LABEL_ROW_HEIGHT}px;
  line-height: ${LABEL_ROW_HEIGHT}px;
  color: ${colors.NEUTRAL_50};
  white-space: nowrap;
`

const barStyles = (accent: string, barHeight: number) => css`
  width: 100%;
  height: ${barHeight}px;
  flex-shrink: 0;
  border-radius: 2px 2px 0 0;
  background: ${accent};
`

export default GamesPlayedStrip
